import { DASHBOARD_BETA_MERCHANT_IDS, ENVIRONMENT } from '@/config';
import { useRouter } from 'vue-router';
import { NetworkError } from '../errors';
import { getMe } from '../services/auth';
import { useUserStore } from '../stores/users';
import { addMetric, createException, createWarning } from './monitoring.utils';
import { getDashboardUrl } from './url.utils';

/**
 * Route guard that prevents already authenticated users from accessing sign-in pages.
 * If the user is already logged in, redirects them to their dashboard or a specified redirect URL.
 * If authentication fails, allows access to the sign-in page and resets the user store.
 *
 * @param to - The target route object containing path, query parameters, and route metadata
 * @returns Promise<string | boolean | void> - Returns redirect URL (string), true to allow navigation, or void to block
 */
export const authGuard = async (to: any) => {
  const userStore = useUserStore();
  const redirect = to.query.redirect as string;
  const getChannelFromUrl = to.path.split('/');
  const channel = getChannelFromUrl[1] || 'shopper';
  try {
    const user = await getMe();
    userStore.user = user;

    addMetric('sign-in', { status: 'already_logged_in', channel });
    if (redirect) {
      return redirect;
    } else {
      if (to.name === 'shopperSignIn' || to.name === 'merchantSignIn') {
        window.location.href = getDashboardUrl(userStore.user.role!);
      }
    }
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('sign-in', { status: 'network_error', channel });
    } else if (error.response ?? error.response?.status === 401) {
      addMetric('sign-in', { status: 'not_logged_in', channel });
    } else {
      addMetric('sign-in', { status: 'error', channel });
      createException(error);
    }
    await userStore.signOut();
    return true;
  }
};

// TEMPORARY: Remove this function once the beta testing is complete
export const hasBetaAccess = () => {
  if (!DASHBOARD_BETA_MERCHANT_IDS) {
    return true;
  }
  const betaMerchantIds = DASHBOARD_BETA_MERCHANT_IDS.split(',');
  if (betaMerchantIds.length === 0) {
    return true;
  }
  const userStore = useUserStore();
  if (userStore.user.role !== 'MERCHANT' || !userStore.user.merchantIds) {
    return false;
  }
  const hasBetaAccess = userStore.user.merchantIds?.some((id) => betaMerchantIds.includes(id));
  return hasBetaAccess;
};

export const canGenerateOrderGuard = () => {
  if (ENVIRONMENT === 'production') {
    const router = useRouter();
    router.push({ name: 'shopperSignIn' });
  } else {
    return true;
  }
};
